import type { ClassSubjectRow, ClassSummary, SubjectRow } from '../../../shared/api/classes'
import type { SchoolStaffRow } from '../../../shared/api/staff'

/**
 * E4's decisions, out of the component.
 *
 * 🔴 THE REPO HAS NO DOM RENDERER (OD-60), so a branch made inside a `.tsx` is a
 * branch no test can reach. `teachersModel.ts` and `schoolDashboardModel.ts` are
 * the precedent for every screen in this tier, and this screen has more branches
 * than either: who actually takes a subject, which subjects a class can still be
 * given, and whether a class is set up at all.
 */

/**
 * Who actually takes this subject in this class.
 *
 * 🔴 THE API DELIBERATELY DOES NOT ANSWER THIS, AND THIS IS WHERE IT IS ANSWERED.
 * `ClassSubject.teacherId` is null for the common case — *"the class's own
 * teacher takes it"* — because copying `Class.teacherId` onto every subject row
 * would be a second place for one fact to live, and the first thing to disagree
 * the day a class changes teacher. So the resolution happens once, here, at the
 * point of display.
 *
 * ⚠️ `null` OUT MEANS NOBODY, WHICH IS A REAL AND VISIBLE STATE. A class with no
 * class teacher and a subject with no teacher of its own is a lesson nobody is
 * down to take — exactly the gap this screen exists to surface, so it must not
 * be rendered as a dash and forgotten.
 */
export interface ResolvedTeacher {
  id: string
  name: string | null
  /** `true` when this is the class teacher standing in rather than a named one. */
  inherited: boolean
}

export function subjectTeacher(
  row: ClassSubjectRow,
  classTeacher: ClassSummary['teacher'],
): ResolvedTeacher | null {
  if (row.teacher !== null) return { ...row.teacher, inherited: false }
  if (classTeacher !== null) return { ...classTeacher, inherited: true }
  return null
}

/**
 * The subjects this class could still be given.
 *
 * 🔴 D4 — A CONTROL THAT CANNOT WORK IS WORSE THAN NO CONTROL. The server
 * answers 409 for a subject the class already studies, so offering it in the
 * picker is a dropdown entry whose only outcome is an error. Filtering here is
 * not the control — `SubjectsService.addToClass` still refuses — it is what
 * stops an admin choosing something that was never available.
 */
export function addableSubjects(
  all: readonly SubjectRow[],
  alreadyOn: readonly ClassSubjectRow[],
): SubjectRow[] {
  const taken = new Set(alreadyOn.map((r) => r.subjectId))
  return all.filter((s) => !taken.has(s.id))
}

/**
 * The teachers an admin may put in front of a class or a subject.
 *
 * 🔴 IT MIRRORS `resolveTeacher` ON THE SERVER AND IT IS NOT THE CONTROL. Four
 * conditions there: exists · same school · is a TEACHER · is active. The list is
 * same-school by construction, so this applies the other two — and the server
 * still refuses anything else generically, because a client-side filter on a
 * list a client was given is not a security boundary. What it buys is that an
 * admin is never offered a name that would come back refused.
 *
 * ⚠️ COORDINATORS ARE NOT ON IT. A subject teacher is who is responsible for
 * teaching a subject, and `Class.teacherId` can only point at a TEACHER.
 * Widening this would produce a dropdown whose choices the server splits into
 * accepted and refused.
 */
export function assignableTeachers(rows: readonly SchoolStaffRow[]): SchoolStaffRow[] {
  return rows.filter((r) => r.role === 'TEACHER' && r.isActive)
}

/**
 * What is missing before this class can be taught.
 *
 * 🔴 IT IS A LIST OF GAPS, NOT A VALIDITY FLAG. A class with no teacher is not
 * broken — it is a class somebody has not finished setting up, and an admin
 * doing twelve of them in September needs to see which of the twelve to go back
 * to. `schoolNotes` does the same job for the dashboard.
 *
 * ⚠️ ORDER MATTERS AND IT IS THIS ONE. No class teacher comes first because it
 * is the fact everything else falls back to: a subject with no teacher of its
 * own is only a gap once the class has nobody either, and reporting both as
 * equal weight would send an admin to fix six subject rows when one dropdown
 * would have done it.
 */
export type ClassGap = 'NO_TEACHER' | 'NO_SUBJECTS' | 'SUBJECTS_WITHOUT_TEACHER'

export function classGaps(
  klass: ClassSummary,
  subjects: readonly ClassSubjectRow[],
): ClassGap[] {
  const gaps: ClassGap[] = []
  if (klass.teacher === null) gaps.push('NO_TEACHER')
  if (subjects.length === 0) gaps.push('NO_SUBJECTS')
  /*
   * ⚠️ ONLY COUNTS WHEN THE CLASS TEACHER CANNOT COVER IT. With a class teacher
   * in place, a null on a subject row means "they take it", which is the
   * ordinary arrangement rather than a gap.
   */
  if (klass.teacher === null && subjects.some((s) => s.teacher === null)) {
    gaps.push('SUBJECTS_WITHOUT_TEACHER')
  }
  return gaps
}

/**
 * Classes in the order an admin wants to work through them.
 *
 * 🔴 UNFINISHED FIRST, THEN BY NAME. The screen's job in September is "which of
 * my twelve classes still needs something", and a list sorted purely by name
 * makes that a scan of twelve expanders. ⚠️ ARCHIVED ALWAYS LAST AND NEVER
 * HIDDEN — `orderSchoolClasses` makes the same call for the dashboard table:
 * last year's cohort is history a school may still want to open.
 */
export function orderClasses(rows: readonly ClassSummary[]): ClassSummary[] {
  return [...rows].sort((a, b) => {
    const archived = Number(a.archivedAt !== null) - Number(b.archivedAt !== null)
    if (archived !== 0) return archived

    const aNeeds = Number(a.teacher === null)
    const bNeeds = Number(b.teacher === null)
    if (aNeeds !== bNeeds) return bNeeds - aNeeds

    return a.name.localeCompare(b.name, undefined, { numeric: true })
  })
}

/**
 * Is this class editable at all?
 *
 * ⚠️ AN ARCHIVED CLASS IS READ-ONLY, and the server says so too
 * (`SubjectsService.assertClassLive`, `ClassesService.update`). Its children have
 * left; changing what it studies would rewrite a record of a finished year.
 */
export function isEditable(klass: ClassSummary): boolean {
  return klass.archivedAt === null
}

export type ClassStatusFilter = 'ALL' | 'ACTIVE' | 'ARCHIVED'
/** `''` is every teacher; `'NONE'` is the classes nobody has been given. */
export type ClassTeacherFilter = string

export interface ClassFilters {
  query: string
  status: ClassStatusFilter
  teacherId: ClassTeacherFilter
  grade: string
}

export const NO_TEACHER = 'NONE'

/**
 * The Classes toolbar, applied.
 *
 * ⚠️ `NO_TEACHER` IS A VALUE OF ITS OWN, NOT AN EMPTY STRING. "All teachers"
 * and "no teacher" are opposite questions, and the second is the one E4 exists
 * to answer.
 */
export function filterClasses(rows: readonly ClassSummary[], f: ClassFilters): ClassSummary[] {
  const q = f.query.trim().toLowerCase()
  return rows.filter((c) => {
    if (f.status === 'ACTIVE' && c.archivedAt !== null) return false
    if (f.status === 'ARCHIVED' && c.archivedAt === null) return false
    if (f.teacherId === NO_TEACHER && c.teacher !== null) return false
    if (f.teacherId !== '' && f.teacherId !== NO_TEACHER && c.teacher?.id !== f.teacherId) return false
    if (f.grade !== '' && c.grade !== f.grade) return false
    if (q === '') return true
    return (
      c.name.toLowerCase().includes(q) ||
      (c.grade?.toLowerCase().includes(q) ?? false) ||
      (c.teacher?.name?.toLowerCase().includes(q) ?? false)
    )
  })
}

/** Every distinct grade on the list, in the order a school counts them. */
export function gradeOptions(rows: readonly ClassSummary[]): string[] {
  const grades = new Set<string>()
  for (const c of rows) if (c.grade !== null && c.grade.trim() !== '') grades.add(c.grade)
  return [...grades].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
}

/**
 * The filtered class list as CSV.
 *
 * 🔴 A LEADING `=`, `+`, `-` OR `@` IS QUOTED OUT. A class name is typed by a
 * school, and a spreadsheet opens a cell that starts with one as a formula —
 * `studentsToCsv`'s guard, byte for byte.
 */
export function classesToCsv(rows: readonly ClassSummary[]): string {
  const esc = (v: string | number): string => {
    const s = String(v)
    const safe = /^[=+\-@\t\r]/.test(s) ? `'${s}` : s
    return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe
  }
  const lines = [['Class', 'Grade', 'Section', 'Band', 'Children', 'Teacher', 'Status'].map(esc).join(',')]
  for (const c of rows) {
    lines.push(
      [
        c.name,
        c.grade ?? '',
        c.section ?? '',
        c.ageBand,
        c.childCount,
        c.teacher?.name ?? '',
        c.archivedAt === null ? 'Active' : 'Archived',
      ]
        .map(esc)
        .join(','),
    )
  }
  return lines.join('\r\n')
}
